import { Link } from "react-router-dom";
import type { Book, StudioSummary } from "../api";
import { useT, type Messages } from "../i18n";
import { summaryPart } from "../studio/translation";
import { AccessBadge } from "./BookTile";

/**
 * BookCard renders one book. Every element carrying authored text has
 * dir="auto": the catalogue is Persian-first and the same page can hold both
 * scripts, so direction is per-string, not per-page.
 *
 * The spine down the leading edge carries the sage-to-sky ramp from the mark.
 * It uses logical `start`, which follows the *card's* direction — the
 * interface's, left in English and right in Persian (ADR-0023) — and
 * deliberately not the direction of the title inside it. The spine is
 * furniture, so it follows the chrome; only authored text follows its script.
 *
 * The whole card is the hit target, via a stretched link rather than an onClick
 * on the <li>. That keeps exactly one link in the accessibility tree, keeps the
 * URL preview on hover and "open in new tab" on middle-click, and avoids nesting
 * anything interactive inside a clickable div. Actions passed as children sit
 * above the overlay on their own stacking context, so they stay clickable.
 */
export default function BookCard({
  book,
  href,
  children,
}: {
  book: Book;
  /** Where the card leads. Defaults to the reader's contents page. */
  href?: string;
  children?: React.ReactNode;
}) {
  const t = useT();
  return (
    <li
      data-testid="book-card"
      className="group relative overflow-hidden rounded-card border border-line bg-surface p-4 transition-colors hover:border-line-strong focus-within:border-sage"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 start-0 w-1 bg-linear-to-b from-sage-deep via-sage to-sky opacity-70 transition-opacity group-hover:opacity-100"
      />
      <div className="flex items-start gap-3 ps-2">
        <div className="min-w-0 flex-1">
          <h2 dir="auto" className="text-lg leading-snug font-medium tracking-tight">
            <Link
              to={href ?? `/books/${book.id}`}
              className="after:absolute after:inset-0 after:content-[''] hover:text-sage-deep"
            >
              {book.title}
            </Link>
          </h2>
          {book.author && (
            <p dir="auto" className="mt-0.5 text-sm text-muted">
              {book.author}
            </p>
          )}
          {book.description && (
            <p dir="auto" className="mt-2 line-clamp-3 text-sm text-ink-soft">
              {book.description}
            </p>
          )}
          {/* On the studio's list: what needs doing, so the list is a to-do
              list rather than a directory. */}
          {book.studio && (
            <p data-testid="book-work" className="mt-2 text-xs text-muted">
              {workLine(book.studio, t)}
            </p>
          )}
        </div>
        {!book.published && (
          <span
            data-testid="draft-badge"
            className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
          >
            {t.library.draft}
          </span>
        )}
        {/* Published for its owner only (ADR-0152): nobody else sees this card. */}
        {book.published && book.visibility === "private" && (
          <span
            data-testid="only-you-badge"
            className="shrink-0 rounded-full bg-sunk px-2 py-0.5 text-xs font-medium text-ink-soft"
          >
            {t.library.onlyYou}
          </span>
        )}
        {/* Shared with chosen groups (ADR-0153): the owner's mark, and the
            reader's — who sees it because a group they are in was chosen. */}
        {book.published && book.visibility === "shared" && !book.shared_with_you && (
          <span
            data-testid="shared-badge"
            className="shrink-0 rounded-full bg-sunk px-2 py-0.5 text-xs font-medium text-ink-soft"
          >
            {t.library.shared}
          </span>
        )}
        <AccessBadge book={book} className="shrink-0" />
        {book.shared_with_you && (
          <span
            data-testid="shared-with-you-badge"
            className="shrink-0 rounded-full bg-sage-soft px-2 py-0.5 text-xs font-medium text-sage-deep"
          >
            {t.library.sharedWithYou}
          </span>
        )}
      </div>
      {/* relative + z-10 lifts the actions out from under the stretched link;
          without it the overlay swallows every tap on them.

          w-fit is the other half, and is easy to miss: a full-width row above
          the link makes the empty space *beside* the buttons dead — it is over
          the link but is not itself a target, so a tap there does nothing at
          all. Shrinking the row to its buttons hands that strip back to the
          card. */}
      {children && <div className="relative z-10 mt-3 flex w-fit gap-2 ps-2">{children}</div>}
    </li>
  );
}

/**
 * workLine says what a book still needs: chapters not narrated, audio behind
 * the text, narration in progress, changes to publish. A book that needs
 * nothing says so, which is the line worth reaching.
 */
export function workLine(w: StudioSummary, t: Messages): string {
  const parts = [t.book.summaryChapters(w.chapters)];
  // A book still being translated is never "all done".
  const translating = summaryPart(w.translation, t);
  if (translating) parts.push(translating);
  const notNarrated = w.chapters - w.narrated;
  if (w.narrating > 0) parts.push(t.book.summaryNarrating(w.narrating));
  if (notNarrated - w.narrating > 0)
    parts.push(t.book.summaryNotNarrated(notNarrated - w.narrating));
  if (w.audio_outdated > 0) parts.push(t.book.summaryOutdated(w.audio_outdated));
  if (w.changes_to_publish > 0) parts.push(t.book.summaryToPublish(w.changes_to_publish));
  if (parts.length === 1 && w.chapters > 0) parts.push(t.book.summaryAllDone);
  return parts.join(" · ");
}
